import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServicesOverviewSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n?.language === 'ar';

  const serviceStats = [
    { label: t('services.activePlayers'), value: '500+', icon: 'Users', gradient: 'from-blue-600 to-blue-700' },
    { label: t('services.partnerClubs'), value: '120+', icon: 'Building', gradient: 'from-emerald-600 to-emerald-700' },
    { label: t('services.countries'), value: '18', icon: 'Globe', gradient: 'from-purple-600 to-purple-700' },
    { label: t('services.successRate'), value: '94%', icon: 'Award', gradient: 'from-amber-600 to-amber-700' }
  ];

  const services = [
    {
      icon: 'Users',
      title: t('services.playerRepresentation'),
      description: t('services.playerRepresentationDesc'),
      features: [t('services.contractNegotiation'), t('services.careerPlanning'), t('services.legalSupport'), t('services.brandManagement')],
      gradient: 'from-blue-600 to-blue-700',
      bgGradient: 'from-blue-50 to-blue-100/50',
      iconColor: 'text-blue-600'
    },
    {
      icon: 'TrendingUp',
      title: t('services.transferFacilitation'),
      description: t('services.transferFacilitationDesc'),
      features: [t('services.clubMatching'), t('services.documentation'), t('services.visaSupport'), t('services.settlementAssistance')],
      gradient: 'from-emerald-600 to-emerald-700',
      bgGradient: 'from-emerald-50 to-emerald-100/50',
      iconColor: 'text-emerald-600'
    },
    {
      icon: 'BarChart3',
      title: t('services.marketIntelligence'),
      description: t('services.marketIntelligenceDesc'),
      features: [t('services.marketAnalysis'), t('services.playerValuation'), t('services.trendReports'), t('services.opportunityAlerts')],
      gradient: 'from-purple-600 to-purple-700',
      bgGradient: 'from-purple-50 to-purple-100/50',
      iconColor: 'text-purple-600'
    },
    {
      icon: 'GraduationCap',
      title: t('services.careerDevelopment'),
      description: t('services.careerDevelopmentDesc'),
      features: [t('services.skillsTraining'), t('services.languageCourses'), t('services.culturalIntegration'), t('services.mentorshipPrograms')],
      gradient: 'from-amber-600 to-amber-700',
      bgGradient: 'from-amber-50 to-amber-100/50',
      iconColor: 'text-amber-600'
    },
    {
      icon: 'Shield',
      title: t('services.complianceLegal'),
      description: t('services.complianceLegalDesc'),
      features: [t('services.fifaCompliance'), t('services.workPermits'), t('services.contractReview'), t('services.disputeResolution')],
      gradient: 'from-red-600 to-red-700',
      bgGradient: 'from-red-50 to-red-100/50',
      iconColor: 'text-red-600'
    },
    {
      icon: 'Globe',
      title: t('services.globalNetwork'),
      description: t('services.globalNetworkDesc'),
      features: [t('services.clubPartnerships'), t('services.scoutNetwork'), t('services.trialArrangements'), t('services.showcaseEvents')],
      gradient: 'from-indigo-600 to-indigo-700',
      bgGradient: 'from-indigo-50 to-indigo-100/50',
      iconColor: 'text-indigo-600'
    },
    {
      icon: 'Target',
      title: t('services.talentScouting'),
      description: t('services.talentScoutingDesc'),
      features: [t('services.videoAnalysis'), t('services.performanceMetrics'), t('services.talentDatabase'), t('services.scoutReports')],
      gradient: 'from-cyan-600 to-cyan-700',
      bgGradient: 'from-cyan-50 to-cyan-100/50',
      iconColor: 'text-cyan-600'
    },
    {
      icon: 'FileText',
      title: t('services.contractManagement'),
      description: t('services.contractManagementDesc'),
      features: [t('services.contractDrafting'), t('services.negotiationStrategy'), t('services.termsReview'), t('services.renewalPlanning')],
      gradient: 'from-pink-600 to-pink-700',
      bgGradient: 'from-pink-50 to-pink-100/50',
      iconColor: 'text-pink-600'
    },
    {
      icon: 'Briefcase',
      title: t('services.brandBuilding'),
      description: t('services.brandBuildingDesc'),
      features: [t('services.socialMediaStrategy'), t('services.prManagement'), t('services.sponsorshipDeals'), t('services.imageRights')],
      gradient: 'from-orange-600 to-orange-700',
      bgGradient: 'from-orange-50 to-orange-100/50',
      iconColor: 'text-orange-600'
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.15) 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-100 to-emerald-100 rounded-full mb-6 shadow-sm">
            <Icon name="Briefcase" size={18} className="text-blue-600" />
            <span className="text-sm font-bold text-slate-800">{t('services.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-black text-slate-900 mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-600 via-emerald-600 to-blue-600 bg-clip-text text-transparent">
              {t('services.title')}
            </span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16 md:mb-20">
          {serviceStats?.map((stat, index) => (
            <div
              key={index}
              className="group relative p-6 md:p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat?.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
              
              {/* Icon */}
              <div className={`relative w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br ${stat?.gradient} flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                <Icon name={stat?.icon} size={28} color="white" />
              </div>
              
              {/* Value */}
              <div className="relative text-3xl md:text-4xl font-black text-slate-900 mb-2 number-ltr">{stat?.value}</div>
              
              {/* Label */}
              <div className="relative text-sm md:text-base text-slate-600 font-medium">{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services?.map((service, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
              {/* Gradient Top Border */}
              <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${service?.gradient}`} />
              
              {/* Card Content */}
              <div className="p-6 md:p-8">
                {/* Icon Container */}
                <div className={`relative w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br ${service?.bgGradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-md`}>
                  <Icon name={service?.icon} size={32} className={service?.iconColor} />
                </div>
                
                {/* Title */}
                <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-4 leading-tight">{service?.title}</h3>
                
                {/* Description */}
                <p className="text-slate-600 mb-6 leading-relaxed">{service?.description}</p>
                
                {/* Features List */}
                <ul className="space-y-3">
                  {service?.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-slate-700">
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br ${service?.gradient} flex items-center justify-center mt-0.5`}>
                        <Icon name="Check" size={12} color="white" />
                      </div>
                      <span className="font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Hover Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service?.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none`} />
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="mt-12 md:mt-16 text-center">
          <Button
            variant="default"
            size="lg"
            iconName={isRTL ? "ArrowLeft" : "ArrowRight"}
            iconPosition={isRTL ? "left" : "right"}
            onClick={() => navigate('/services-overview')}
            className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white shadow-xl shadow-blue-500/30 border-0 text-base font-bold px-10 py-6">
            {t('services.viewAllServices')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverviewSection;